<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed, onMounted, ref, watch } from 'vue';

import { useAuthStore } from '@/stores/authStore.ts';
import { displayToast } from '@/utils/toastUtils.ts';
import { displayModal } from '@/utils/modalUtils.ts';
import { useDrawer } from '@/stores/useDrawer.ts';
import UserService from '@/services/user/userService.ts';
import MainTitleComponent from '@/components/ui/MainTitleComponent.vue';
import DeleteUserComponent from '@/components/user/DeleteUserComponent.vue';
import { formatItemValue, formatUserStatus } from '@/utils/formatters.ts';

import type { DeleteUserRequest, User, UserSortKey, UserStatusKey } from '@/types/services/user/user.type.ts';
import type { PaginatedQuery, SortItem, SortOrder } from '@/types/services/utils/request.type.ts';
import type { PaginatedResponse } from '@/types/services/utils/response.type.ts';
import type { DataTableHeader } from 'vuetify/framework';
import type { ModalOptions } from '@/types/stores/modalStore.type.ts';
import UpdateDamView from '@/views/users/dam/UpdateDamView.vue';
import CreateDamView from '@/views/users/dam/CreateDamView.vue';

const { t } = useI18n()
const { openDrawer } = useDrawer()
const authStore = useAuthStore()

const users = ref<User[]>([])
const loading = ref<boolean>(false)

const search = ref<string>('')
const sort = ref<UserSortKey>('email')
const order = ref<SortOrder>('asc')
const limit = ref<number>(10)
const page = ref<number>(1)

const total = ref<number>(0)
const maxPages = ref<number>(1)

const headers = computed<DataTableHeader[]>(() => [
  {
    title: t('common.userList.header.email'),
    key: 'email',
    sortable: true,
    align: 'start'
  },
  {
    title: t('common.userList.header.firstName'),
    key: 'firstName',
    sortable: true,
    align: 'start'
  },
  {
    title: t('common.userList.header.lastName'),
    key: 'lastName',
    sortable: true,
    align: 'start'
  },
  {
    title: t('common.userList.header.role'),
    key: 'role',
    sortable: true,
    align: 'start'
  },
  {
    title: t('common.userList.header.status'),
    key: 'status',
    sortable: true,
    align: 'start'
  },
  {
    title: t('common.userList.header.actions'),
    key: 'actions',
    sortable: false,
    width: '100px',
    align: 'end'
  },
])

onMounted(fetchUsers);

watch([search, sort, order, limit, page], fetchUsers);

function hasPermission(permission: string[]): boolean {
  return authStore.hasPermission(permission);
}

async function handleCreateDam(): Promise<void> {
  openDrawer({
    title: t('views.createDam.title'),
    component: CreateDamView,
    props: {
      drawerMode: true,
      onCreated: async () => {
        await fetchUsers()
        useDrawer().closeDrawer()
      }
    },
    width: 520,
  })
}

async function fetchUsers(): Promise<void> {
  try {
    loading.value = true;

    const params: PaginatedQuery = {
      search: search.value,
      sort: sort.value,
      order: order.value,
      limit: limit.value,
      page: page.value
    };

    const response: PaginatedResponse<User> = await UserService.getPaginatedDam(params);

    users.value = response.data;
    total.value = response.total;
    maxPages.value = response.maxPages;
  } finally {
    loading.value = false;
  }
}

function onSort(sortBy: SortItem<UserSortKey>[]): void {
  const first = sortBy?.[0];
  sort.value = first.key;
  order.value = first.order;
}

async function onRowClick(_: MouseEvent, row: { item: User }): Promise<void> {
  if (hasPermission(['EDIT_USER_DAM'])) {
    openUserDetail(row.item)
  }
}

function openUserDetail(user: User) {
  openDrawer({
    title: t('views.updateDam.title'),
    component: UpdateDamView,
    props: {
      key: user.id + '-' + Date.now(),
      userId: user.id,
      drawerMode: true,
      onUpdated: async () => {
        await fetchUsers()
        useDrawer().closeDrawer()
      }
    },
    width: 520,
  })
}

function formatRoleValue(value: string): string {
  return value ? t(`common.role.${value}`) : t('common.noData');
}

async function updateStatus(user: User, status: UserStatusKey): Promise<void> {
  const username: string = `${user.firstName} ${user.lastName}`

  let title: string = t('common.userList.reactivateUser.title')
  let message: string = t('common.userList.reactivateUser.message', { username: username })
  let success: string = t('common.userList.reactivateSuccess')

  if (status == 'inactive') {
    title = t('common.userList.deactivateUser.title')
    message = t('common.userList.deactivateUser.message', { username: username })
    success = t('common.userList.deactivateSuccess')
  }

  const options: ModalOptions = { title, message }
  const modal = await displayModal(options)

  if (modal) {
    await UserService.updateDamStatus(user.id, status)
    displayToast(success, 'success')
    await fetchUsers()
  }
}

async function deleteUser(user: User): Promise<void> {
  const modal = await displayModal({
    component: DeleteUserComponent,
    props: {
      user: user
    },
  });

  if (modal && typeof modal === 'object') {
    const { reason } = modal as DeleteUserRequest;
    await UserService.anonymize(user.id, reason);
    displayToast(t('common.userList.deleteSuccess'), 'success');
    await fetchUsers();
  }
}

async function resendInvitation(user: User): Promise<void> {
  const username: string = `${user.firstName} ${user.lastName}`

  const options: ModalOptions = {
    title: t('common.userList.resendInvitation.title'),
    message: t('common.userList.resendInvitation.message', { username: username })
  }
  const modal = await displayModal(options)

  if (modal) {
    await UserService.resendDamInvitation(user.id)
    displayToast(t('common.userList.resendInvitationSuccess'), 'success')
  }
}
</script>

<template>
  <!-- Main title -->
  <MainTitleComponent icon="PhUsers">
    {{ t("views.damList.title") }}
  </MainTitleComponent>

  <v-row justify="space-between" align="center">
    <v-col cols="12" sm="5">
      <v-text-field
        v-model="search"
        :label="t('views.damList.search')"
        append-inner-icon="PhMagnifyingGlass"
        density="compact"
        hide-details
      />
    </v-col>

    <v-col v-if="hasPermission(['ADD_USER_DAM'])" cols="12" sm="auto">
      <v-btn
        class="w-100 w-sm-auto"
        @click.prevent="handleCreateDam"
      >
        {{ t("views.damList.buttons.create") }}
        <template #prepend>
          <v-icon icon="PhPlus" />
        </template>
      </v-btn>
    </v-col>
  </v-row>

  <v-row>
    <v-col>
      <v-data-table-server
        :headers="headers"
        :items="users"
        :items-length="total"
        :items-per-page="limit"
        :loading="loading"
        :no-data-text="t('common.noResult')"
        :page="page"
        :sort-by="[{ key: sort, order: order }]"
        hover
        must-sort
        @update:page="page = $event"
        @update:items-per-page="limit = $event"
        @update:sort-by="onSort"
        @click:row="onRowClick"
      >
        <template #loading>
          <v-skeleton-loader type="table-row@4" />
        </template>
        <template #[`item.lastname`]="{ item }">
          {{ formatItemValue(item.lastName) }}
        </template>
        <template #[`item.firstname`]="{ item }">
          {{ formatItemValue(item.firstName) }}
        </template>
        <template #[`item.email`]="{ item }">
          {{ formatItemValue(item.email) }}
        </template>
        <template #[`item.role`]="{ item }">
          {{ formatRoleValue(item.role) }}
        </template>
        <template #[`item.status`]="{ item }">
          <v-chip
            :color="formatUserStatus(item.status)"
            text-color="white"
            class="ma-2"
          >
            {{ t('common.userList.status.' + item.status) }}
          </v-chip>
        </template>
        <template #[`item.actions`]="{ item }">
          <v-menu
            v-if="hasPermission(['EDIT_USER_DAM', 'DELETE_USER_DAM', 'ANONYMIZE_USER', 'ADD_USER_DAM'])"
          >
            <template #activator="{ props }">
              <button type="button" v-bind="props">
                <v-icon icon="PhDotsThreeVertical" />
              </button>
            </template>

            <v-list>
              <v-list-item
                v-if="hasPermission(['EDIT_USER_DAM'])"
                @click.prevent="openUserDetail(item)"
              >
                <template #default>
                  <p class="text-subtitle-2">
                    {{ t('common.userList.table.actions.updateUser') }}
                  </p>
                </template>
                <template #prepend>
                  <v-icon size="small" icon="PhPencil" />
                </template>
              </v-list-item>
              <v-list-item
                v-if="hasPermission(['ADD_USER_DAM'])"
                @click.prevent="resendInvitation(item)"
              >
                <template #default>
                  <p class="text-subtitle-2">
                    {{ t('common.userList.table.actions.resendInvitation') }}
                  </p>
                </template>
                <template #prepend>
                  <v-icon size="small" icon="PhPaperPlaneTilt" />
                </template>
              </v-list-item>
              <v-list-item
                v-if="item.status == 'active' && hasPermission(['DELETE_USER_DAM'])"
                @click.prevent="updateStatus(item, 'inactive')"
              >
                <template #default>
                  <p class="text-subtitle-2">
                    {{ t('common.userList.table.actions.deactivateUser') }}
                  </p>
                </template>
                <template #prepend>
                  <v-icon size="small" icon="PhProhibit" />
                </template>
              </v-list-item>
              <v-list-item
                v-if="item.status == 'inactive' && hasPermission(['DELETE_USER_DAM'])"
                @click.prevent="updateStatus(item, 'active')"
              >
                <template #default>
                  <p class="text-subtitle-2">
                    {{ t('common.userList.table.actions.reactivateUser') }}
                  </p>
                </template>
                <template #prepend>
                  <v-icon size="small" icon="PhCheckCircle" />
                </template>
              </v-list-item>
              <v-list-item
                v-if="item.status == 'inactive' && hasPermission(['ANONYMIZE_USER'])"
                @click.prevent="deleteUser(item)"
              >
                <template #default>
                  <p class="text-subtitle-2">
                    {{ t('common.userList.table.actions.deleteUser') }}
                  </p>
                </template>
                <template #prepend>
                  <v-icon size="small" icon="PhTrash" />
                </template>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
        <template #bottom>
          <v-pagination
            v-model="page"
            :length="maxPages"
            :tile="true"
            density="compact"
            total-visible="6"
          />
        </template>
      </v-data-table-server>
    </v-col>
  </v-row>
</template>
